import React, { useState, useRef, useEffect } from "react";
import { connect } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";

// Functional component for Menu
const Menu = (props) => {
  const { user, company, showMenu, leftMenuButtons, setClick } = props;
  const [showOffCanvas, setShowOffCanvas] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState({});
  const offCanvasRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname } = location;

  // Equivalent to componentDidMount
  useEffect(() => {
    if (setClick) {
      setClick(toggleMenu);
    }

    // Set up the navigation handler for external calls
    window.changeReactMenuHandler = navigateToHandler;

    // Add escape key handler to close the menu
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setShowOffCanvas(false);
      }
    };
    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const navigateToHandler = (to) => {
    navigate(to);
  };

  const toggleMenu = () => {
    setShowOffCanvas(true);
    return false;
  };

  const handleClose = () => {
    setShowOffCanvas(false);
  };

  const getCompany = () => {
    let retVal = false;
    if (user && user.company && user.company.id > 0) {
      retVal = user.company;
    }
    return retVal;
  };

  // Check if a path is active
  const isActive = (path) => {
    return pathname === path ? "bg-gray-50" : "hover:bg-gray-50";
  };

  // Toggle a dropdown
  const toggleDropdown = (id) => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Define all menu items in a single array for easy management
  const menuClassName = "text-gray-400 size- w-6 h-6 shrink-0";
  const menuItems = [
    {
      id: "dashboard",
      label: "Accountoverzicht",
      path: "/",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={menuClassName}
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M4 9L12 2.5L20 9V20H4V9Z"
            stroke="currentColor"
            strokeWidth="1.5"
          />
        </svg>
      ),
      requiresUser: false,
    },
    {
      id: "profile",
      label: "Mijn profiel",
      path: "/profile/",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={menuClassName}
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M6.16318 18.5C7.49405 16.8187 9.52996 15.75 12 15.75C14.47 15.75 16.506 16.8187 17.8368 18.5M15.25 10C15.25 11.7949 13.7949 13.25 12 13.25C10.2051 13.25 8.75 11.7949 8.75 10C8.75 8.20507 10.2051 6.75 12 6.75C13.7949 6.75 15.25 8.20507 15.25 10ZM21.25 12C21.25 6.89137 17.1086 2.75 12 2.75C6.89137 2.75 2.75 6.89137 2.75 12C2.75 14.7509 3.95086 17.2214 5.85697 18.9157C7.49061 20.3679 9.6423 21.25 12 21.25C14.3577 21.25 16.5094 20.3679 18.143 18.9157C20.0491 17.2214 21.25 14.7509 21.25 12Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
        </svg>
      ),
      requiresUser: false,
    },
    {
      id: "company",
      label: "Mijn bedrijven",
      path: "/company/",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={menuClassName}
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M4 19H14M4 19V4H14V8M4 19H2M20 19H14M20 19V8H14M20 19H22M14 19V8M10 9H8M8 13H10"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeMiterlimit="16"
            strokeLinecap="square"
          />
        </svg>
      ),
      requiresUser: true,
    },
    {
      id: "products",
      label: "Mijn advertenties",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={menuClassName}
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M10.75 21.25H4.75V2.75H19.25V11.75"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="square"
          />
          <path
            d="M13.75 21.2502V18.9168L17.5 15.1668C18.1443 14.5225 19.189 14.5225 19.8333 15.1668C20.4777 15.8112 20.4777 16.8558 19.8333 17.5002L16.0833 21.2502H13.75Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="square"
          />
          <path
            d="M8.75 6.75H15.25"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="square"
          />
          <path
            d="M8.75 10.75H11.25"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="square"
          />
        </svg>
      ),
      requiresCompanyType: "PROVIDER",
      isDropdown: true,
      dropdownItems: [
        {
          id: "materials",
          label: "Mijn advertenties",
          path: "/products/",
        },
        {
          id: "reservations",
          label: "Reserveringen",
          path: "/products/reservation/",
        },
      ],
    },
    {
      id: "my-reservations",
      label: "Geplaatste reserveringen",
      path: "/products/my-reservations/",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={menuClassName}
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M12 12L5.75 8V2.75H18.25V8L12 12ZM12 12L18.25 16V21.25H5.75V16L12 12ZM20.25 21.25H3.75M20.25 2.75H3.75"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="square"
          />
        </svg>
      ),
      requiresUser: false,
    },
    {
      id: "logout",
      label: "Uitloggen",
      path: "/logout/",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={menuClassName}
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M11.25 20.25H3.75L3.75 3.75L11.25 3.75M9 12L19.5 12M15.75 16.5L20.25 12L15.75 7.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="square"
          />
        </svg>
      ),
      requiresUser: false,
    },
  ];

  // Render a regular menu item
  const renderMenuItem = (item, isMobile = false) => {
    // Skip if it requires a user and we don't have one
    if (item.requiresUser && !user) return null;

    // Skip if it requires a specific company type and conditions aren't met
    if (
      item.requiresCompanyType &&
      (!getCompany() || getCompany().type !== item.requiresCompanyType)
    )
      return null;

    // Render dropdown menu
    if (item.isDropdown) {
      const isOpen = openDropdowns[item.id] || false;

      return (
        <li key={item.id}>
          <div>
            <button
              type="button"
              className="flex gap-x-3 items-center p-2 w-full font-semibold text-left text-gray-700 hover:bg-gray-50 text-sm/6"
              aria-controls={`sub-menu-${isMobile ? "mobile-" : ""}${item.id}`}
              onClick={() => toggleDropdown(item.id)}
              aria-expanded={isOpen}
            >
              {item.icon}
              {item.label}
              <svg
                className={`ml-auto size-5 w-5 h-5 shrink-0 ${
                  isOpen ? "text-gray-500 rotate-90" : "text-gray-400"
                }`}
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
            {isOpen && (
              <ul
                className="px-2 mt-1"
                id={`sub-menu-${isMobile ? "mobile-" : ""}${item.id}`}
              >
                {item.dropdownItems.map((subItem) => (
                  <li key={subItem.id}>
                    <Link
                      to={subItem.path}
                      onClick={isMobile ? () => handleClose() : undefined}
                      className={`hover:bg-gray-50 block py-2 pr-2 pl-9 text-sm/6 text-gray-700 ${
                        pathname === subItem.path ? "font-medium" : ""
                      }`}
                    >
                      {subItem.label}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </li>
      );
    }

    // Render regular menu item
    return (
      <li key={item.id}>
        <Link
          to={item.path}
          onClick={isMobile ? () => handleClose() : undefined}
          className={`${isActive(
            item.path
          )} group flex gap-x-3 p-2 text-sm/6 font-semibold text-gray-700`}
        >
          {item.icon}
          {item.label}
        </Link>
      </li>
    );
  };

  // Handle sidebar click outside
  const handleClickOutside = (e) => {
    if (
      showOffCanvas &&
      offCanvasRef.current &&
      !offCanvasRef.current.contains(e.target)
    ) {
      handleClose();
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showOffCanvas]);

  return (
    <div className="w-fit">
      {/* Mobile menu toggle button - fixed at bottom */}
      {showMenu && (
        <>
          <button
            onClick={toggleMenu}
            className="flex fixed left-0 bottom-5 z-30 gap-1.5 justify-center items-center px-4 py-2.5 text-sm font-semibold text-white lg:hidden bg-primary"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              className="text-white size-5"
            >
              <path
                d="M17.5 9C17.5 5.68629 14.8137 3 11.5 3C8.18629 3 5.5 5.68629 5.5 9C5.5 9.70127 5.62031 10.3744 5.84141 11M13 12V9C13 8.17157 12.3284 7.5 11.5 7.5C10.6716 7.5 10 8.17157 10 9V13.5L9.94413 13.4721C8.89757 12.9488 7.6273 13.4317 7.19275 14.5181L7 15L9.81253 19.2188C10.5544 20.3316 11.8033 21 13.1407 21H14C16.7614 21 19 18.7614 19 16V15C19 13.3431 17.6569 12 16 12H13Z"
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>

            <span>Sidebar menu</span>
          </button>
        </>
      )}
      {/* Desktop sidebar - always visible on xl screens */}
      <div className="hidden h-full lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
        <div className="flex overflow-y-auto flex-col gap-y-5 py-10 bg-white border-r border-gray-200 pe-6 grow">
          {/* User info section */}
          {user && (
            <div className="flex gap-x-4 items-center py-3">
              {user.image?.imageUrl ? (
                <img
                  className="bg-gray-50 rounded-full ring-1 ring-gray-200 size-10"
                  src={user.image.imageUrl}
                  alt=""
                />
              ) : (
                <span className="inline-flex justify-center items-center bg-gray-500 rounded-full size-8">
                  <span className="text-sm font-medium text-white">
                    {user._firstname?.substring(0, 2).toUpperCase()}
                  </span>
                </span>
              )}
              <div>
                <div className="text-sm font-semibold text-gray-900">
                  {user._firstname} {user._insertion} {user._lastname}
                </div>
                {user.company && (
                  <div className="text-xs text-gray-500">
                    {user.company.name}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Navigation menu */}
          <nav className="flex flex-col flex-1">
            <ul role="list" className="flex flex-col flex-1 gap-y-7">
              <li>
                <ul role="list" className="-mx-2 space-y-1">
                  {/* Render all menu items from the array */}
                  {menuItems.map((item) => renderMenuItem(item))}
                </ul>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Mobile sidebar - offcanvas component */}
      {showOffCanvas && (
        <div className="relative z-50 xl:hidden" aria-modal="true">
          {/* Background overlay */}
          <div
            className="fixed inset-0 bg-black/80"
            onClick={handleClose}
          ></div>

          {/* Sliding sidebar panel */}
          <div className="flex fixed inset-0">
            <div
              className="flex relative flex-1 mr-16 w-full max-w-xs"
              ref={offCanvasRef}
            >
              {/* Close button */}
              <div className="flex absolute top-0 left-full justify-center pt-3 w-16">
                <button
                  type="button"
                  className="p-2.5 -m-2.5"
                  onClick={handleClose}
                >
                  <span className="sr-only">Close sidebar</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={menuClassName}
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M5 5L19 19M19 5L5 19"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="square"
                    />
                  </svg>
                </button>
              </div>

              {/* Sidebar content */}
              <div className="flex overflow-y-auto flex-col gap-y-5 px-6 py-4 bg-white grow">
                {/* Mobile navigation menu */}
                <nav className="flex flex-col flex-1">
                  <ul role="list" className="-mx-2 space-y-1">
                    {/* Render all menu items from the array, passing isMobile=true */}
                    {menuItems.map((item) => renderMenuItem(item, true))}
                  </ul>
                </nav>
                {/* User info section */}
                {user && (
                  <div className="flex fixed bottom-0 gap-x-4 items-center py-3 w-full">
                    {user.image?.imageUrl ? (
                      <img
                        className="bg-gray-50 rounded-full size-8"
                        src={user.image.imageUrl}
                        alt=""
                      />
                    ) : (
                      <span className="inline-flex justify-center items-center bg-gray-500 rounded-full size-8">
                        <span className="text-sm font-medium text-white">
                          {user._firstname?.substring(0, 2).toUpperCase()}
                        </span>
                      </span>
                    )}
                    <div>
                      <div className="text-sm font-semibold text-gray-900">
                        {user._firstname} {user._insertion} {user._lastname}
                      </div>
                      {user.company && (
                        <div className="text-xs text-gray-500">
                          {user.company.name}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Wrapper component for react-router integration
const MenuInit = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  return <Menu {...props} navigate={navigate} location={location} />;
};

function mapStateToProps(state) {
  const { isLoggedIn, user, company } = state.auth;
  const { show, leftButtons } = state.menu;
  return {
    isLoggedIn,
    user: user,
    company: company,
    showMenu: show,
    leftMenuButtons: leftButtons,
  };
}

export default connect(mapStateToProps)(MenuInit);
